import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Plus, Edit, Trash2, ToggleLeft, ToggleRight, Eye } from 'lucide-react';
import popupApi from '../../api/popupApi';
import PopupForm from '../../components/admin/popup/PopupForm';
import PopupTable from '../../components/admin/popup/PopupTable';

const PopupManagement = () => {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPopup, setEditingPopup] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchPopups();
  }, [currentPage, searchTerm, statusFilter]);

  const fetchPopups = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        enabled: statusFilter === 'all' ? undefined : statusFilter === 'enabled'
      };

      const response = await popupApi.getPopups(params);
      setPopups(response.popups);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Error fetching popups:', error);
      toast.error('Failed to fetch popups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPopup(null);
    setShowForm(true);
  };

  const handleEdit = (popup) => {
    setEditingPopup(popup);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this popup?')) return;

    try {
      await popupApi.deletePopup(id);
      toast.success('Popup deleted successfully');
      fetchPopups();
    } catch (error) {
      console.error('Error deleting popup:', error);
      toast.error('Failed to delete popup');
    }
  };

  const handleToggle = async (id) => {
    try {
      await popupApi.togglePopup(id);
      toast.success('Popup status updated');
      fetchPopups();
    } catch (error) {
      console.error('Error toggling popup:', error);
      toast.error('Failed to update popup status');
    }
  };

 const handleFormSubmit = async (formData) => {
  try {
    const backgroundImage = formData.get('backgroundImage');
    const hasFile = backgroundImage instanceof File && backgroundImage.size > 0;

    const data = {};
    if (!hasFile) {
      for (let [key, value] of formData.entries()) {
        if (key === 'backgroundImage') continue;
        if (key.startsWith('ctas[')) {
          const match = key.match(/ctas\[(\d+)\]\[(\w+)\]/);
          if (match) {
            const [_, index, field] = match;
            if (!data.ctas) data.ctas = [];
            if (!data.ctas[index]) data.ctas[index] = {};
            data.ctas[index][field] = field === 'primary' ? value === 'true' : value;
          }
        } else if (key.startsWith('dailySchedule[')) {
          const field = key.match(/dailySchedule\[(\w+)\]/)[1];
          if (!data.dailySchedule) data.dailySchedule = {};
          data.dailySchedule[field] = field === 'enabled' ? value === 'true' : value;
        } else if (key === 'enabled' || key === 'closable') {
          data[key] = value === 'true';
        } else if (key === 'priority' || key === 'displayDuration' || key === 'delaySeconds') {
          data[key] = parseInt(value, 10);
        } else if (key === 'startDate' || key === 'endDate') {
          data[key] = new Date(value);
        } else {
          data[key] = value;
        }
      }
    }

    if (editingPopup) {
      await popupApi.updatePopup(editingPopup._id, hasFile ? formData : data);
      toast.success('Popup updated successfully');
    } else {
      await popupApi.createPopup(hasFile ? formData : data);
      toast.success('Popup created successfully');
    }
    setShowForm(false);
    fetchPopups();
  } catch (error) {
    console.error('Error saving popup:', error);
    toast.error(error.response?.data?.message || 'Failed to save popup');
  }
};

const handleFormCancel = () => {
    setShowForm(false);
    setEditingPopup(null);
  };

  if (showForm) {
    return (
      <PopupForm
        popup={editingPopup}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Popup Management</h1>
          <p className="text-gray-600">Manage promotional popups and alerts</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus size={20} />
          Create Popup
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <input
          type="text"
          placeholder="Search popups..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="enabled">Enabled</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      {/* Table */}
      <PopupTable
        popups={popups}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggle={handleToggle}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PopupManagement;
